import { useState } from 'react'
import { Settings, X, Eye, EyeOff, Plus, Maximize2, Minimize2 } from 'lucide-react'
import { LineSwatch, LineStyleSelect, LineWidthSelect } from './LineControls.jsx'
import { resolveConfig, DEFAULT_LINE_STYLE, DEFAULT_LINE_WIDTH } from './constants.js'

function BadgeSection({ title, open, onToggle, children }) {
  return (
    <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
      <button
        onClick={onToggle}
        className="w-full pt-1.5 pb-1 flex items-center justify-between"
        aria-expanded={open}
      >
        <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>{title}</span>
        <span className="text-[10px] font-medium" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && children}
    </div>
  )
}

function parsePeriod(value) {
  const num = parseInt(value, 10)
  if (isNaN(num) || num < 1) return 1
  if (num > 9999) return 9999
  return num
}

export default function EmaSmaBadge({
  config = {},
  onChange,
  addMovingAverage,
  removeMovingAverage,
  toggleMovingAverage,
  updateMovingAverage,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0.0',
  onHeaderMouseDown,
}) {
  // resolveConfig (E.2): las medias guardadas antes de v16 no traen
  // lineStyle/lineWidth — se sanean aquí en el punto de consumo, no solo en
  // el hook, para que nunca lleguen undefined al swatch ni a los selectores.
  const mas = resolveConfig(config).mas
  const [activeSection, setActiveSection] = useState('list')
  const [addEmaSection, setAddEmaSection] = useState({ open: false, period: 20, color: '#a855f7', lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH })
  const [addSmaSection, setAddSmaSection] = useState({ open: false, period: 200, color: '#22c55e', lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH })
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})

  const toggleSection = (key) => setActiveSection(prev => prev === key ? '' : key)

  const handleAddEma = () => {
    const period = parsePeriod(addEmaSection.period)
    const exists = mas.some(ma => ma.type === 'ema' && ma.period === period)
    if (exists) {
      // Dedup por tipo+periodo (invariante): una media existente con el
      // mismo tipo+periodo se reactiva tal cual, sin aplicar el estilo del
      // formulario — dos medias con mismo tipo+periodo nunca coexisten con
      // estilos distintos, la ya guardada manda.
      const existing = mas.find(ma => ma.type === 'ema' && ma.period === period)
      if (existing && !existing.active) {
        updateMovingAverage(existing.id, { active: true })
      }
    } else {
      addMovingAverage('ema', { period, color: addEmaSection.color, lineStyle: addEmaSection.lineStyle, lineWidth: addEmaSection.lineWidth })
    }
    setAddEmaSection({ open: false, period: 20, color: '#a855f7', lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH })
  }

  const handleAddSma = () => {
    const period = parsePeriod(addSmaSection.period)
    const exists = mas.some(ma => ma.type === 'sma' && ma.period === period)
    if (exists) {
      const existing = mas.find(ma => ma.type === 'sma' && ma.period === period)
      if (existing && !existing.active) {
        updateMovingAverage(existing.id, { active: true })
      }
    } else {
      addMovingAverage('sma', { period, color: addSmaSection.color, lineStyle: addSmaSection.lineStyle, lineWidth: addSmaSection.lineWidth })
    }
    setAddSmaSection({ open: false, period: 200, color: '#22c55e', lineStyle: DEFAULT_LINE_STYLE, lineWidth: DEFAULT_LINE_WIDTH })
  }

  const startEdit = (ma) => {
    setEditingId(ma.id)
    setEditForm({ type: ma.type, period: ma.period, color: ma.color, lineStyle: ma.lineStyle ?? DEFAULT_LINE_STYLE, lineWidth: ma.lineWidth ?? DEFAULT_LINE_WIDTH })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({})
  }

  const saveEdit = (maId) => {
    const currentMa = mas.find(m => m.id === maId)
    if (!currentMa) return

    const period = parsePeriod(editForm.period)
    const typeChanged = editForm.type !== currentMa.type
    const periodChanged = period !== currentMa.period

    if (typeChanged || periodChanged) {
      const existing = mas.find(ma => ma.id !== maId && ma.type === editForm.type && ma.period === period)
      if (existing) {
        if (!existing.active) {
          updateMovingAverage(existing.id, { active: true })
        }
        cancelEdit()
        return
      }
    }

    updateMovingAverage(maId, { ...editForm, period })
    cancelEdit()
  }

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px] max-w-[280px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          EMA/SMA Quantum <span className="opacity-60 font-normal">[{version}]</span>
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {typeof onMinimize === 'function' && (minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        ))}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <BadgeSection title="Medias activas" open={activeSection === 'list'} onToggle={() => toggleSection('list')}>
            {mas.length === 0 && (
              <div className="text-[9px] text-center py-1" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                Sin medias móviles
              </div>
            )}
            {mas.map(ma => (
              <div key={ma.id} className="flex items-center gap-1.5 py-1">
                {editingId === ma.id ? (
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <select
                        value={editForm.type}
                        onChange={e => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                        className="text-[9px] px-1 py-0.5 border rounded"
                        style={{ flexShrink: 0, color: 'var(--ink, #15211B)', background: 'var(--paper, #FDFDFA)' }}
                      >
                        <option value="ema">EMA</option>
                        <option value="sma">SMA</option>
                      </select>
                      <input
                        type="number"
                        value={editForm.period || ''}
                        onChange={e => setEditForm(prev => ({ ...prev, period: e.target.value }))}
                        className="text-[9px] px-1 py-0.5 border rounded w-16"
                        style={{ color: 'var(--ink, #15211B)', background: 'var(--paper, #FDFDFA)' }}
                        min="1"
                        max="9999"
                      />
                      <input
                        type="color"
                        value={editForm.color}
                        onChange={e => setEditForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-5 h-5 p-0 border-0 rounded cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1">
                        <LineStyleSelect compact value={editForm.lineStyle ?? DEFAULT_LINE_STYLE} onChange={(lineStyle) => setEditForm(prev => ({ ...prev, lineStyle }))} />
                      </div>
                      <div className="flex-1">
                        <LineWidthSelect compact value={editForm.lineWidth ?? DEFAULT_LINE_WIDTH} onChange={(lineWidth) => setEditForm(prev => ({ ...prev, lineWidth }))} />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => saveEdit(ma.id)}
                        className="flex-1 text-[8px] px-1 py-0.5 rounded bg-green-600 text-white"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="flex-1 text-[8px] px-1 py-0.5 rounded border"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => toggleMovingAverage(ma.id, !ma.active)}
                      className="p-0.5"
                      title={ma.active ? 'Ocultar' : 'Mostrar'}
                    >
                      {ma.active
                        ? <Eye size={10} style={{ color: 'var(--ink, #15211B)' }} />
                        : <EyeOff size={10} style={{ color: 'var(--ink-soft, #4A5A52)' }} />}
                    </button>
                    <LineSwatch color={ma.color} lineStyle={ma.lineStyle} lineWidth={ma.lineWidth} width={22} height={10} />
                    <button
                      onClick={() => startEdit(ma)}
                      className="flex-1 text-left"
                    >
                      <span className={`text-[9px] font-medium ${ma.active ? '' : 'opacity-50'}`} style={{ color: ma.active ? 'var(--ink, #15211B)' : 'var(--ink-soft, #4A5A52)' }}>
                        {ma.type.toUpperCase()} {ma.period}
                      </span>
                    </button>
                    <button
                      onClick={() => removeMovingAverage(ma.id)}
                      className="p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                      style={{ color: 'var(--ink-soft, #4A5A52)' }}
                      title="Eliminar"
                    >
                      <X size={10} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </BadgeSection>

          <BadgeSection title="+EMA" open={addEmaSection.open} onToggle={() => setAddEmaSection(prev => ({ ...prev, open: !prev.open }))}>
            <div className="flex flex-col gap-1.5 py-1">
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Periodo:</label>
                <input
                  type="number"
                  value={addEmaSection.period}
                  onChange={e => setAddEmaSection(prev => ({ ...prev, period: e.target.value }))}
                  className="flex-1 text-[9px] px-1.5 py-1 border rounded"
                  style={{ color: 'var(--ink, #15211B)', background: 'var(--paper, #FDFDFA)' }}
                  min="1"
                  max="9999"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Color:</label>
                <input
                  type="color"
                  value={addEmaSection.color}
                  onChange={e => setAddEmaSection(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-6 p-0 border rounded cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Estilo:</label>
                <div className="flex-1">
                  <LineStyleSelect compact value={addEmaSection.lineStyle} onChange={(lineStyle) => setAddEmaSection(prev => ({ ...prev, lineStyle }))} />
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Grosor:</label>
                <div className="flex-1">
                  <LineWidthSelect compact value={addEmaSection.lineWidth} onChange={(lineWidth) => setAddEmaSection(prev => ({ ...prev, lineWidth }))} />
                </div>
              </div>
              <button
                onClick={handleAddEma}
                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded bg-[var(--green-deep,#0B3D2E)] text-white hover:opacity-90 transition-opacity"
              >
                <Plus size={10} /> Añadir EMA
              </button>
            </div>
          </BadgeSection>

          <BadgeSection title="+SMA" open={addSmaSection.open} onToggle={() => setAddSmaSection(prev => ({ ...prev, open: !prev.open }))}>
            <div className="flex flex-col gap-1.5 py-1">
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Periodo:</label>
                <input
                  type="number"
                  value={addSmaSection.period}
                  onChange={e => setAddSmaSection(prev => ({ ...prev, period: e.target.value }))}
                  className="flex-1 text-[9px] px-1.5 py-1 border rounded"
                  style={{ color: 'var(--ink, #15211B)', background: 'var(--paper, #FDFDFA)' }}
                  min="1"
                  max="9999"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Color:</label>
                <input
                  type="color"
                  value={addSmaSection.color}
                  onChange={e => setAddSmaSection(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-6 p-0 border rounded cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Estilo:</label>
                <div className="flex-1">
                  <LineStyleSelect compact value={addSmaSection.lineStyle} onChange={(lineStyle) => setAddSmaSection(prev => ({ ...prev, lineStyle }))} />
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)', width: '50px' }}>Grosor:</label>
                <div className="flex-1">
                  <LineWidthSelect compact value={addSmaSection.lineWidth} onChange={(lineWidth) => setAddSmaSection(prev => ({ ...prev, lineWidth }))} />
                </div>
              </div>
              <button
                onClick={handleAddSma}
                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded bg-[var(--green-deep,#0B3D2E)] text-white hover:opacity-90 transition-opacity"
              >
                <Plus size={10} /> Añadir SMA
              </button>
            </div>
          </BadgeSection>
        </>
      )}
    </div>
  )
}
